<template>
  <q-page class="column no-wrap bg-white">
    <!-- ONLY SHOW APP ONCE INITIALIZED -->
    <template v-if="isSessionInitialized">
    <!-- OBSERVER HEADER -->
    <q-toolbar class="col-auto bg-primary text-white shadow-2" style="z-index: 10">
      <div>
        <q-select
          v-model="gameId"
          :options="gameOptions"
          label="Select Game"
          dense
          outlined
          dark
          options-dense
          emit-value
          map-options
          @update:model-value="appInit"
          style="min-width: 150px"
          color="white"
        />
      </div>
      <q-space />
      <q-btn-toggle
        v-model="activeView"
        no-caps
        dense
        unelevated
        toggle-color="white"
        toggle-text-color="primary"
        text-color="grey-4"
        class="bg-primary-dark"
        :options="[
          { label: 'Field', value: 'field', icon: 'sports_baseball' },
          { label: 'Roster', value: 'roster', icon: 'list' },
        ]"
      />
      <q-space />
      <q-btn color="info" icon="settings" unelevated @click="showSettings = true" class="q-mr-sm" />
      <q-btn color="secondary" icon="download" unelevated @click="exportData" />
    </q-toolbar>

    <!-- INNING / CONTEXT ENGINE -->
    <div
      v-if="selectedPlayerIds.length <= 0 || showDefenseSheet"
      class="col-auto bg-white q-pa-sm row items-center justify-between shadow-1"
      style="z-index: 5"
    >
      <div class="row items-center q-gutter-x-sm">
        <q-btn flat round dense icon="remove" @click="adjustInning(-1)" />
        <div class="text-weight-bold text-subtitle1">
          Inning {{ currentInning }}
        </div>
        <q-btn flat round dense icon="add" @click="adjustInning(1)" />
      </div>
      <div class="row items-center q-gutter-x-sm">
        <q-btn flat dense @click="openDefenseSheet" class="q-px-sm text-primary">
          <div
            class="row items-center no-wrap text-weight-bolder"
            style="font-size: 1.1rem; letter-spacing: 1px"
          >
            D<q-icon name="fence" size="sm" style="margin-left: -4px" />
          </div>
        </q-btn>
        <q-toggle v-model="showNames" label="Show Names" dense left-label color="primary" />
      </div>
    </div>

    <!-- MAIN CONTENT STACK (Strict Flexbox) -->
    <div class="col" style="display: flex; flex-direction: column; overflow: hidden">
      <!-- TOP: FIELD / ROSTER (Allowed to shrink) -->
      <div style="flex: 1 1 150px; min-height: 150px; position: relative; overflow: hidden">
        <ObserverFieldView
          v-if="activeView === 'field'"
          class="absolute-full"
          :defense-snapshot="activeDefenseSnapshot"
          :lineup="activeDefensiveLineup"
          :selected-player-ids="selectedPlayerIds"
          :show-names="showNames"
          @toggle-selection="togglePlayerSelection"
        />
        <ObserverRosterView
          v-else
          class="absolute-full"
          :defense-snapshot="activeDefenseSnapshot"
          :lineup="activeDefensiveLineup"
          :selected-player-ids="selectedPlayerIds"
          @toggle-selection="togglePlayerSelection"
        />
      </div>

      <!-- BOTTOM: EVALUATION DRAWER -->
      <div
        v-if="selectedPlayerIds.length > 0"
        class="shadow-up-3 bg-white"
        style="
          display: flex;
          flex-direction: column;
          flex: 0 1 auto;
          max-height: 75vh;
          min-height: 0;
          z-index: 20;
          border-radius: 16px 16px 0 0;
          overflow: hidden;
        "
      >
        <!-- 1. DRAWER HEADER (Fixed, does not scroll) -->
        <div class="q-pa-sm border-bottom" style="flex: 0 0 auto">
          <div class="row items-center justify-between q-mb-xs">
            <div class="text-subtitle2 text-weight-bold text-primary">
              Evaluating {{ selectedPlayerIds.length }} Player(s)
            </div>
            <q-btn
              flat
              dense
              color="negative"
              label="Clear All"
              icon="clear_all"
              @click="clearSelection"
            />
          </div>
          <!-- Selected Players -->
          <div class="row wrap" style="gap: 4px">
            <q-chip
              v-for="playerId in selectedPlayerIds"
              :key="'selected-' + playerId"
              removable
              color="primary"
              text-color="white"
              dense
              class="q-ma-none"
              @remove="togglePlayerSelection(playerId)"
            >
              {{ getPlayerChipLabel(playerId) }}
            </q-chip>
          </div>
        </div>

        <!-- 2. SCROLLABLE BODY (Contains Draft Queue, Tags, Categories, and Note) -->
        <div
          class="q-pa-md"
          style="flex: 1 1 auto; min-height: 0; overflow-y: auto; overflow-x: hidden"
        >
          <!-- DRAFT QUEUE -->
          <div v-if="draftEvaluations.length > 0" class="q-mb-md">
            <div class="text-caption text-weight-bold text-primary text-uppercase q-mb-xs">
              Draft Queue ({{ draftEvaluations.length }})
            </div>
            <q-list separator class="border-all rounded-borders bg-grey-1">
              <q-item v-for="draft in draftEvaluations" :key="draft.id" class="q-py-sm">
                <!-- min-width: 0 prevents horizontal blowouts inside the list items -->
                <q-item-section style="min-width: 0">
                  <div class="row wrap" style="gap: 4px; margin-bottom: 4px">
                    <q-chip
                      v-for="tag in draft.tags"
                      :key="draft.id + '-' + tag"
                      size="sm"
                      :color="getCategoryColor(draft.category)"
                      text-color="white"
                      class="q-ma-none text-weight-bold ellipsis"
                    >
                      {{ getTagLabel(tag) }}
                    </q-chip>
                  </div>
                  <q-item-label caption v-if="draft.note" class="text-italic text-grey-8 ellipsis">
                    "{{ draft.note }}"
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn
                    flat
                    round
                    dense
                    icon="remove_circle_outline"
                    color="negative"
                    size="sm"
                    @click="removeFromDraft(draft.id)"
                  />
                </q-item-section>
              </q-item>
            </q-list>
          </div>

          <!-- QUICK TAGS -->
          <div class="text-caption text-weight-bold text-grey-8 q-mb-sm">Select Tags:</div>
          <div class="row wrap q-mb-md" style="gap: 8px">
            <q-btn
              v-for="tag in settingsStore.activeScoutingTags"
              :key="tag.value"
              :label="tag.label"
              :color="selectedTags.includes(tag.value) ? tag.color || 'primary' : 'grey-2'"
              :text-color="selectedTags.includes(tag.value) ? 'white' : 'grey-8'"
              unelevated
              rounded
              size="sm"
              class="text-weight-bold"
              @click="selectTag(tag.value)"
            />
          </div>

          <!-- CATEGORY -->
          <div
            v-if="selectedTags.length > 0"
            class="text-caption text-weight-bold text-grey-8 q-mb-sm"
          >
            Overall Sentiment:
          </div>
          <div v-if="selectedTags.length > 0" class="row wrap q-mb-md" style="gap: 8px">
            <q-btn
              v-for="opt in categoryOptions"
              :key="opt.value"
              :label="opt.label"
              :color="selectedCategory === opt.value ? opt.color : 'grey-2'"
              :text-color="selectedCategory === opt.value ? 'white' : 'grey-8'"
              unelevated
              rounded
              size="sm"
              class="col-auto col-sm text-weight-bold"
              @click="selectedCategory = opt.value"
            />
          </div>

          <!-- OPTIONAL CUSTOM NOTE -->
          <q-input
            v-if="selectedTags.length > 0"
            v-model="customNote"
            label="Optional Note..."
            outlined
            dense
            autogrow
            class="q-mb-none"
          >
            <template v-slot:prepend>
              <q-icon name="edit_note" />
            </template>
          </q-input>
        </div>

        <!-- 3. STICKY FOOTER (Fixed at the bottom of the drawer) -->
        <div class="q-pa-md bg-white border-top" style="flex: 0 0 auto">
          <div class="row q-col-gutter-sm">
            <div class="col-12 col-sm-6">
              <q-btn
                label="Add To Draft"
                color="secondary"
                icon="add_box"
                class="full-width text-weight-bold"
                outline
                :disable="selectedTags.length === 0 || !selectedCategory"
                @click="addToDraft"
              />
            </div>
            <div class="col-12 col-sm-6">
              <q-btn
                :label="`Save All (${draftEvaluations.length + (selectedTags.length > 0 ? 1 : 0)})`"
                color="primary"
                icon="save"
                class="full-width text-weight-bold"
                unelevated
                :disable="draftEvaluations.length === 0 && selectedTags.length === 0"
                @click="saveAllDrafts"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
      <!-- BOTTOM SHEET 1: DEFENSE OVERVIEW (DRAFT MODE) -->
      <q-dialog v-model="showDefenseSheet" position="top" persistent>
        <q-card
          class="full-width bg-white column no-wrap"
          style="max-height: 85vh; border-radius: 0 0 16px 16px; overflow: hidden"
        >
          <q-card-section
            class="row items-center justify-between col-auto bg-grey-2 border-bottom z-top"
          >
            <div class="text-subtitle1 text-weight-bold text-primary">Edit Defense</div>
            <q-btn icon="close" flat round dense v-close-popup />
          </q-card-section>
          <q-card-section class="col scroll q-pa-none">
            <q-list separator>
              <q-item
                v-for="pos in 9"
                :key="'pos-' + pos"
                clickable
                v-ripple
                @click="openSwapMenu(pos)"
                class="q-py-md"
              >
                <q-item-section avatar class="q-pr-sm" style="min-width: 70px">
                  <q-chip
                    color="primary"
                    text-color="white"
                    class="text-weight-bold shadow-1"
                    size="sm"
                  >
                    {{ settingsStore.getPositionShortLabel(pos) }}
                  </q-chip>
                </q-item-section>
                <q-item-section>
                  <q-item-label
                    class="text-weight-bold text-body1"
                    :class="{ 'text-positive': isPositionChanged(pos) }"
                  >
                    #{{ getDraftPlayerAtPosition(pos)?.uniformNumber || '-' }}
                  </q-item-label>
                  <q-item-label
                    caption
                    v-if="getDraftPlayerAtPosition(pos)"
                    :class="{ 'text-positive': isPositionChanged(pos) }"
                  >
                    {{ getDraftPlayerAtPosition(pos)?.name || 'Empty' }}
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-btn flat round dense icon="swap_horiz" color="grey-7" />
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
          <q-card-actions align="between" class="col-auto bg-white border-top q-pa-md z-top">
            <q-btn flat label="Discard" color="negative" v-close-popup />
            <q-btn
              unelevated
              label="Save Defense"
              color="primary"
              icon-right="check"
              :disable="!hasUnsavedChanges"
              @click="commitDefenseChanges"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>

      <!-- BOTTOM SHEET 2: ROSTER SELECTION -->
      <q-dialog v-model="showSwapDialog" position="bottom">
        <q-card
          class="full-width bg-white column no-wrap"
          style="max-height: 65vh; border-radius: 16px 16px 0 0; overflow: hidden"
        >
          <q-card-section
            class="row items-center justify-between shrink bg-grey-2 border-bottom z-top"
          >
            <div class="text-subtitle1 text-weight-bold">
              Select For {{ settingsStore.getPositionShortLabel(activeSwapPosition || 0) }}
            </div>
            <q-btn icon="close" flat round dense v-close-popup />
          </q-card-section>
          <q-card-section class="col scroll q-pa-none">
            <q-list separator>
              <q-item
                v-for="player in availableDefensivePlayers"
                :key="'swap-' + player.id"
                clickable
                v-ripple
                @click="executeLocalSwap(player.id)"
              >
                <q-item-section>
                  <q-item-label
                    :class="{ 'text-primary text-weight-bold': player.label.includes('(Current)') }"
                  >
                    {{ player.label }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-card-section>
        </q-card>
      </q-dialog>

      <q-dialog v-model="showSettings" position="top" persistent>
        <q-card
          class="full-width bg-white column no-wrap"
          style="max-height: 95vh; border-radius: 0 0 16px 16px; overflow: hidden"
        >
          <!-- Sticky Header -->
          <q-card-section
            class="row items-center justify-between col-auto bg-grey-2 border-bottom z-top"
          >
            <div class="text-subtitle1 text-weight-bold text-primary">Edit</div>
            <q-btn icon="close" flat round dense v-close-popup />
          </q-card-section>

          <!-- Make this section scrollable by adding "col scroll" -->
          <q-card-section class="col scroll q-pb-xl">
            <UserSettingsScouting
              :game-id="gameId"
              :coach-initials="coachInitials"
            ></UserSettingsScouting>
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- COACH INITIALS PROMPT -->
      <q-dialog v-model="showInitialsDialog" persistent>
        <q-card style="min-width: 300px; border-radius: 12px;">
          <q-card-section class="bg-primary text-white">
            <div class="text-subtitle1 text-weight-bold">Enter Initials</div>
          </q-card-section>

          <q-card-section class="q-pt-md">
            <div class="text-caption text-grey-8 q-mb-sm">
              Please enter your initials to save these observations to the cloud.
            </div>
            <q-input
              v-model="coachInitials"
              label="Initials (e.g., JD)"
              outlined
              dense
              autofocus
              @keyup.enter="confirmAndSave"
            />
          </q-card-section>

          <q-card-actions align="right" class="text-primary">
            <q-btn flat label="Cancel" v-close-popup />
            <q-btn flat label="Save Observations" @click="confirmAndSave" :disable="!coachInitials.trim()" />
          </q-card-actions>
        </q-card>
      </q-dialog>
    </template>


    <!-- INITIALS PROMPT (Serves as both Login and Save Gate) -->
    <q-dialog v-model="showInitialsDialog" persistent>
      <q-card style="min-width: 300px; border-radius: 12px;">
        <q-card-section class="bg-primary text-white">
          <div class="text-subtitle1 text-weight-bold">
            {{ isSessionInitialized ? 'Enter Initials' : 'Welcome to Scout Mode' }}
          </div>
        </q-card-section>

        <q-card-section class="q-pt-md">
          <div class="text-caption text-grey-8 q-mb-sm">
            {{ isSessionInitialized ? 'Please enter your initials to save these observations.' : 'Enter your initials to load your custom settings and begin.' }}
          </div>
          <q-input
            v-model="coachInitials"
            label="Initials (e.g., JD)"
            outlined
            dense
            autofocus
            @keyup.enter="handleInitialsSubmit"
          />
        </q-card-section>

        <q-card-actions align="right" class="text-primary">
          <!-- Only allow cancel if the app is already initialized -->
          <q-btn v-if="isSessionInitialized" flat label="Cancel" v-close-popup />
          <q-btn
            flat
            :label="isSessionInitialized ? 'Save Observations' : 'Start Scouting'"
            @click="handleInitialsSubmit"
            :disable="!coachInitials.trim()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useQuasar } from 'quasar'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { useAuthStore } from 'stores/auth-store'
import ObserverFieldView from 'components/ObserverFieldView.vue'
import ObserverRosterView from 'components/ObserverRosterView.vue'
import UserSettingsScouting from 'components/UserSettingsScouting.vue'
import { generateRocksLineup } from 'src/helpers/generators'
import type { DefenseSnapshot, ScoutingTransaction, LineupEntry, Position } from 'stores/base-types'
import { db } from 'src/models/db'
import { supabase } from 'src/services/supabase'

// --- NEW: DRAFT TYPES ---
interface DraftEval {
  id: string
  tags: string[]
  category: 'positive' | 'negative' | 'neutral'
  note: string
}

const route = useRoute()
const $q = useQuasar()
const settingsStore = useSettingsStore()
const gameStore = useGameStore()
const authStore = useAuthStore()

// --- LIFECYCLE ---
onMounted( async () => {
  showInitialsDialog.value = true
  // Fetch Demo Lineups / Games for the dropdown
  try {
    const { data, error } = await supabase.from('demo_lineups').select('*')
    if (error) throw error
    if (data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      gameOptions.value = data.map((row: any) => ({
        label: `Game: ${row.game_id}`,
        value: String(row.game_id)
      }))
    }
  } catch (err) {
    console.error('Failed to load demo_lineups for dropdown', err)
  }
  if (gameOptions.value.length >= 1) {
    gameId.value = gameOptions.value[0]!.value
  } else {
    gameId.value = route.params.gameId as string
  }
})

// --- HYDRATION STATE ---
const gameId = ref('')
const gameOptions = ref<{ label: string; value: string }[]>([])
const trackedLineup = ref<LineupEntry[]>([])

// --- DUMMY PA ENGINE ---
const showNames = ref(true)

const categoryOptions: {
  label: string
  value: 'positive' | 'negative' | 'neutral'
  color: string
}[] = [
  { label: 'Positive', value: 'positive', color: 'positive' },
  { label: 'Neutral', value: 'neutral', color: 'grey-7' },
  { label: 'Negative', value: 'negative', color: 'negative' },
]

const showSettings = ref(false)
const currentInning = ref(1)
const activeDefenseSnapshot = ref<DefenseSnapshot>({})

// --- UI STATE ---
const isSessionInitialized = ref(false)
const activeView = ref<'field' | 'roster'>('field')
const selectedPlayerIds = ref<number[]>([])
const selectedTags = ref<string[]>([])
const selectedCategory = ref<'positive' | 'negative' | 'neutral' | null>('neutral')
const customNote = ref('')
const draftEvaluations = ref<DraftEval[]>([])
// --- NEW REFS FOR COACH ATTRIBUTION ---
const coachInitials = ref('')
const showInitialsDialog = ref(false)
const isCloudSyncFailing = ref(false) // Tracks if we need to show the manual export dialog

const appInit = async () => {
  gameStore.appContext = 'OBSERVER'
  gameStore.plateAppearances = []
  gameStore.transactions = []

  authStore.loadGuestProfile()
  await hydrateDemoFromSupabase() // Will now use coachInitials.value
  await loadOrCreateInningContext()
}

const handleInitialsSubmit = async () => {
  if (!coachInitials.value.trim()) return

  showInitialsDialog.value = false
  coachInitials.value = coachInitials.value.trim().toUpperCase() // Standardize uppercase

  if (!isSessionInitialized.value) {
    // --- APP STARTUP FLOW ---
    await appInit()

    isSessionInitialized.value = true
  } else {
    // --- SAVE ALL FLOW ---
    await saveAllDrafts()
  }
}


const confirmAndSave = async () => {
  if (!coachInitials.value.trim()) return
  showInitialsDialog.value = false
  await saveAllDrafts() // Re-trigger the save now that we have initials
}



const activeDefensiveLineup = computed(() => {
  return gameStore.visitingLineup
})

const activeDummyPA = computed(() => {
  const observerPAs = gameStore.plateAppearances.filter(
    (pa) => pa.context === 'OBSERVER' && pa.inning === currentInning.value
  )
  return observerPAs[observerPAs.length - 1]
})

// --- UI HELPERS ---
const getPlayerChipLabel = (playerId: number) => {
  const allRoster = activeDefensiveLineup.value.concat(
    activeDefensiveLineup.value.flatMap((l) => l.substitutions || []),
  )
  const entry = allRoster.find((p) => p.player.id === playerId)
  const nameStr = entry ? `#${entry.player.uniformNumber || '-'} ${entry.player.name}` : 'Unknown'

  let posLabel = 'Bench'
  for (const [posStr, id] of Object.entries(activeDefenseSnapshot.value)) {
    const pId = typeof id === 'object' ? id?.playerId : id
    if (pId === playerId) {
      posLabel = settingsStore.getPositionShortLabel(Number(posStr))
      break
    }
  }

  return `${nameStr} (${posLabel})`
}

const getTagLabel = (tagValue: string) => {
  const found = settingsStore.activeScoutingTags.find((t) => t.value === tagValue)
  return found ? found.label : tagValue
}

const getCategoryColor = (cat: string) => {
  if (cat === 'positive') return 'positive'
  if (cat === 'negative') return 'negative'
  return 'grey-6'
}

// --- ENGINE LOGIC ---
const rebuildDefenseSnapshot = () => {
  const snap: DefenseSnapshot = {}
  activeDefensiveLineup.value.forEach((entry) => {
    if (entry.startingPosition >= 1 && entry.startingPosition <= 9) {
      snap[entry.startingPosition as Position] = {
        ...snap[entry.startingPosition as Position],
        ...{ playerId: entry.player.id! },
      }
    }
  })
  activeDefenseSnapshot.value = snap
}

const generateDummyPA = async () => {
  await gameStore.createPlateAppearanceDummy(
    gameId.value,
    currentInning.value,
    'top', // Hardcoded to satisfy store type expectations
    activeDefenseSnapshot.value,
  )
}

const loadOrCreateInningContext = async () => {
  const existingPA = gameStore.plateAppearances
    .slice()
    .reverse()
    .find((pa) => pa.context === 'OBSERVER' && pa.inning === currentInning.value)

  if (existingPA) {
    activeDefenseSnapshot.value = { ...(existingPA.defenseSnapshot || {}) }
  } else {
    const priorPA = gameStore.plateAppearances
      .slice()
      .reverse()
      .find((pa) => pa.context === 'OBSERVER')

    if (priorPA && priorPA.defenseSnapshot && Object.keys(priorPA.defenseSnapshot).length > 0) {
      activeDefenseSnapshot.value = { ...priorPA.defenseSnapshot }
    } else {
      rebuildDefenseSnapshot()
    }
    await generateDummyPA()
  }
}

const adjustInning = async (amount: number) => {
  const next = currentInning.value + amount
  if (next < 1) return
  currentInning.value = next
  await loadOrCreateInningContext()
  clearSelection()
}

// --- EVALUATION LOGIC ---
const togglePlayerSelection = (playerId: number) => {
  const priorLength = selectedPlayerIds.value.length
  const idx = selectedPlayerIds.value.indexOf(playerId)
  if (idx > -1) selectedPlayerIds.value.splice(idx, 1)
  else selectedPlayerIds.value.push(playerId)

  if (priorLength === 0 && selectedPlayerIds.value.length === 1) {
    showNames.value = false
  }
}

const clearSelection = () => {
  selectedPlayerIds.value = []
  selectedTags.value = []
  selectedCategory.value = 'neutral'
  customNote.value = ''
  draftEvaluations.value = []
  showNames.value = true
}

// --- DRAFT INTERACTIONS ---
const addToDraft = () => {
  if (selectedTags.value.length === 0 || !selectedCategory.value) return

  draftEvaluations.value.push({
    id: Date.now().toString() + Math.random().toString(36).substring(2, 6),
    tags: [...selectedTags.value],
    category: selectedCategory.value,
    note: customNote.value.trim(),
  })

  selectedTags.value = []
  selectedCategory.value = 'neutral'
  customNote.value = ''
}

const removeFromDraft = (id: string) => {
  draftEvaluations.value = draftEvaluations.value.filter((d) => d.id !== id)
}

// --- INTERACTIONS ---
const toggleTag = (val: string) => {
  const index = selectedTags.value.indexOf(val)
  if (index > -1) {
    selectedTags.value.splice(index, 1)
  } else {
    selectedTags.value.push(val)
  }
}

const selectTag = (tagValue: string) => {
  toggleTag(tagValue)

  if (selectedTags.value.includes(tagValue)) {
    const tagDef = settingsStore.activeScoutingTags.find((t) => t.value === tagValue)
    if (tagDef?.category) {
      selectedCategory.value = tagDef.category
    }
  }
}

const saveAllDrafts = async () => {
  if (selectedTags.value.length > 0 && selectedCategory.value) {
    addToDraft()
  }

  if (
    !activeDummyPA.value ||
    selectedPlayerIds.value.length === 0 ||
    draftEvaluations.value.length === 0
  )
    return

  // 1. INTERCEPT: Ask for initials if we don't have them yet
  if (!coachInitials.value.trim()) {
    showInitialsDialog.value = true
    return
  }

  try {
    let currentSequence =
      gameStore.transactions.length > 0
        ? Math.max(...gameStore.transactions.map((t) => t.sequence))
        : 0

    // Grab the active settings snapshot for the cloud
    const snapshotSettings = {
      customLabels: authStore.currentUser?.settings?.customLabels || {},
      customCategories: authStore.currentUser?.settings?.customCategories || {},
      hiddenScoutingTags: authStore.currentUser?.settings?.hiddenScoutingTags || []
    }

    const supabasePayload = []

    for (const draft of draftEvaluations.value) {
      for (const tagValue of draft.tags) {
        currentSequence++

        // A. Local Record
        const txn: ScoutingTransaction = {
          id: Date.now().toString() + Math.random().toString(36).substring(2, 6),
          gameId: gameId.value,
          timestamp: Date.now(),
          sequence: currentSequence,
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          plateAppearanceId: activeDummyPA.value.id!,
          context: 'OBSERVER',
          inning: currentInning.value,
          halfInning: 'top',
          batterId: null,
          pitcherId: null,
          isIncomplete: false,
          notes: null,
          activeTransactionFlag: true,
          eventType: 'SCOUTING',
          targetPlayerIds: [...selectedPlayerIds.value],
          tagValue: tagValue,
          tagCategory: draft.category,
          ...(draft.note !== '' && { customNote: draft.note }),
        }

        gameStore.transactions.push(txn)
        await db.transactions.put(JSON.parse(JSON.stringify(txn)))

        // B. Prepare Cloud Payload
        supabasePayload.push({
          game_id: gameId.value,
          coach_initials: coachInitials.value.trim().toUpperCase(),
          player_ids: [...selectedPlayerIds.value],
          tag_value: tagValue,
          category: draft.category,
          note: draft.note || null,
          snapshot_settings: snapshotSettings
        })
      }
    }

    // 2. INCREMENTAL CLOUD SYNC
    const { error: supabaseError } = await supabase
      .from('demo_observations')
      .insert(supabasePayload)

    if (supabaseError) {
      console.error('Incremental Supabase save error:', supabaseError)
      isCloudSyncFailing.value = true // Flip flag so the final export button steps in
      $q.notify({ message: 'Saved locally, but cloud sync failed.', color: 'warning', icon: 'cloud_off' })
    } else {
      isCloudSyncFailing.value = false // Connection is healthy!
      $q.notify({
        message: `Saved ${draftEvaluations.value.length} Evaluations!`,
        color: 'positive',
        icon: 'check_circle',
        position: 'bottom',
      })
    }

  } catch (err) {
    console.error('Failed to save scouting evals', err)
  }
  clearSelection()
}

const exportData = () => {
  // If connection is healthy, hide the dialog. Do a silent cloud push + JSON download.
  if (!isCloudSyncFailing.value) {
    void handleExport(['json', 'cloud'], true)
    return
  }

  // If connection has been failing, prompt them to try cloud again or just take the JSON
  $q.dialog({
    title: 'Sync Error Warning',
    message: 'Some of your recent observations failed to sync to the cloud. How would you like to proceed?',
    options: {
      type: 'checkbox',
      model: ['json'], // Default to just taking the JSON since the network is flaky
      items: [
        { label: 'Retry Cloud Sync', value: 'cloud', color: 'primary' },
        { label: 'Download JSON Backup', value: 'json', color: 'secondary' }
      ]
    },
    cancel: true,
    persistent: true
  }).onOk((selected: string[]) => {
    void handleExport(selected, false)
  })
}

// Separate async helper function
// Separate async helper function (now accepts an `isSilent` flag)
const handleExport = async (selected: string[], isSilent = false) => {
  const doCloud = selected.includes('cloud')
  const doJson = selected.includes('json')

  if (!doCloud && !doJson) return

  // Enforce coach initials if they want to push to the cloud
  if (doCloud && !coachInitials.value.trim()) {
    if (!isSilent) {
      $q.notify({
        message: 'Please save an observation first to set your coach initials!',
        color: 'warning',
        icon: 'person_add'
      })
    }
    showInitialsDialog.value = true
    return
  }

  try {
    if (!isSilent) $q.loading.show({ message: 'Processing...' })

    await gameStore.exportObserverData(gameId.value, {
      syncToCloud: doCloud,
      downloadJson: doJson,
      coachInitials: coachInitials.value.trim()
    })

    if (!isSilent) $q.loading.hide()

    // If the cloud sync finally succeeded, reset our tracker
    if (doCloud) {
      isCloudSyncFailing.value = false
    }

    if (doJson) {
      $q.notify({
        message: 'JSON file downloaded successfully.',
        color: 'info',
        icon: 'download',
        position: 'bottom'
      })
    }

    // Only show the cloud success message if they manually triggered the dialog
    if (doCloud && !isSilent) {
      $q.notify({
        message: 'Cloud Sync Successful!',
        color: 'positive',
        icon: 'cloud_done',
        position: 'bottom'
      })
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    if (!isSilent) $q.loading.hide()

    isCloudSyncFailing.value = true
    $q.notify({
      message: 'Export failed. Ensure you have data to export or check your connection.',
      color: 'negative',
      icon: 'error',
      position: 'bottom'
    })
  }
}

// -- DEFENSE & DRAFT SUBSTITUTION LOGIC (OBSERVER MODE) --
const showDefenseSheet = ref(false)
const showSwapDialog = ref(false)
const activeSwapPosition = ref<number | null>(null)

const draftDefense = ref<DefenseSnapshot>({})
const originalDefense = ref<DefenseSnapshot>({})

const openDefenseSheet = () => {
  originalDefense.value = { ...activeDefenseSnapshot.value }
  draftDefense.value = { ...activeDefenseSnapshot.value }
  showDefenseSheet.value = true
}

const hasUnsavedChanges = computed(() => {
  return JSON.stringify(draftDefense.value) !== JSON.stringify(originalDefense.value)
})

const isPositionChanged = (pos: number) => {
  return draftDefense.value[pos as Position] !== originalDefense.value[pos as Position]
}

const getDraftPlayerAtPosition = (pos: number) => {
  const playerId = draftDefense.value[pos as Position]?.playerId
  if (!playerId) return null

  const allPlayers = activeDefensiveLineup.value.concat(
    activeDefensiveLineup.value.flatMap((l) => l.substitutions || []),
  )
  const lineupEntry = allPlayers.find((p) => p.player.id === playerId)

  return lineupEntry ? lineupEntry.player : null
}

const openSwapMenu = (pos: number) => {
  activeSwapPosition.value = pos
  showSwapDialog.value = true
}

const availableDefensivePlayers = computed(() => {
  const allPlayers = activeDefensiveLineup.value.concat(
    activeDefensiveLineup.value.flatMap((l) => l.substitutions || []),
  )

  return (
    allPlayers.map((e) => {
      let activePos = null
      for (const [posStr, id] of Object.entries(draftDefense.value)) {
        if (id.playerId === e.player.id) {
          activePos = Number(posStr)
          break
        }
      }

      const posLabel = activePos
        ? `- Pos ${settingsStore.getPositionShortLabel(activePos)}`
        : '- Bench'
      const isCurrent = activePos === activeSwapPosition.value ? '(Current)' : ''

      return {
        label: `#${e.player.uniformNumber || '-'} ${e.player.name} ${posLabel} ${isCurrent}`.trim(),
        id: e.player.id,
      }
    }) || []
  )
})

const executeLocalSwap = (playerId?: number) => {
  if (!playerId || !activeSwapPosition.value) return

  for (const [posStr, id] of Object.entries(draftDefense.value)) {
    if (id.playerId === playerId) {
      delete draftDefense.value[Number(posStr) as Position]
    }
  }

  draftDefense.value[activeSwapPosition.value as Position] = {
    ...draftDefense.value[activeSwapPosition.value as Position],
    ...{ playerId: playerId },
  }
  showSwapDialog.value = false
}

const commitDefenseChanges = async () => {
  activeDefenseSnapshot.value = { ...draftDefense.value }
  await generateDummyPA()
  clearSelection()

  showDefenseSheet.value = false
  $q.notify({ message: 'Defense Updated!', color: 'positive', position: 'bottom' })
}

const hydrateDemoFromSupabase = async () => {
  try {
    // A. Fetch Visiting Lineup (Unchanged, linked only to gameId)
    const { data: lineupData, error: lineupError } = await supabase
      .from('demo_lineups')
      .select('lineup_data')
      .eq('game_id', gameId.value)
      .eq('team_type', 'visiting')
      .single()

    if (lineupData && !lineupError) {
      gameStore.visitingLineup = lineupData.lineup_data
      trackedLineup.value = lineupData.lineup_data
    } else {
      gameStore.visitingLineup = generateRocksLineup()
      trackedLineup.value = generateRocksLineup()
    }

    // B. Fetch Settings (NOW FILTERED BY INITIALS)
    const { data: settingsData, error: settingsError } = await supabase
      .from('demo_settings')
      .select('custom_labels, custom_categories, hidden_scouting_tags')
      .eq('game_id', gameId.value)
      .eq('coach_initials', coachInitials.value) // <--- ADDED FILTER
      .maybeSingle()

    if (settingsData && !settingsError) {
      if (authStore.currentUser) {
        authStore.currentUser.settings = {
          ...authStore.currentUser.settings,
          customLabels: settingsData.custom_labels,
          customCategories: settingsData.custom_categories,
          hiddenScoutingTags: settingsData.hidden_scouting_tags
        }
      }
      console.log(`Hydrated custom scouting settings for ${coachInitials.value}!`)
    } else {
      console.log('No existing settings found for these initials. Using defaults.')
    }

  } catch (err) {
    console.warn('Supabase hydration failed, using local defaults.', err)
    gameStore.visitingLineup = generateRocksLineup()
    trackedLineup.value = generateRocksLineup()
  }
}
</script>
